import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Link, Outlet, matchPath, useLocation, useParams, useNavigate } from 'react-router-dom';
import { readFragment } from 'gql.tada';
import { useBootstrapQuery } from './useBootstrapQuery';
import { useAppQuery } from './useAppQuery';
import { SidebarFragment } from '~/components/Sidebar/SidebarFragment';
import { useRoleSelection } from '../../hooks/useRoleSelection';
import { useRoleContext } from '../../hooks/useRoleContext';
import { useSidebarLayout } from '../../hooks/useSidebarLayout';
import Sidebar from '../../components/Sidebar/Sidebar';
import { SidebarCustomizer } from '../../components/Sidebar/SidebarCustomizer';
import { allViews, defaultLayout, roleGroupFor } from '../../components/Sidebar/sidebarLayout';
import { ContextNavProvider } from '../../components/Sidebar/contextNav';
import { useMyFamilyQuery } from '../../pages/MyFamily/useMyFamilyQuery';
import { useSidebarClassesQuery } from '../../components/Sidebar/useSidebarClassesQuery';
import { classesInTerm, defaultTermId, termsFromClasses } from '../../pages/Teaching/teachingTerms';
import Topbar from '../../components/Topbar';
import QuickSearch from '../../pages/Dashboard/QuickSearch';
import type { QuickSearchHandle } from '../../pages/Dashboard/QuickSearch';
import SignOutButton from '../../components/SignOutButton';
import { Button } from '../../components/ui/button';
import './AppLayout.css';

// A family's enroll pages: a year's checklist, which the emailed enrollment
// links open, and each step on it. A step finishes on its own page: the fee is
// paid in a dialog there, and Stripe sends a family adding a payment method
// back to the step it came from.
const ENROLL_PAGES = ['/app/:schoolSlug/years/:yearSlug/enroll', '/app/:schoolSlug/years/:yearSlug/enroll/:itemId'];

function AppLayoutInner() {
  const { schoolSlug, yearSlug } = useParams();
  const { pathname } = useLocation();
  const onEnrollPage = ENROLL_PAGES.some((pattern) => matchPath(pattern, pathname));
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const quickSearchRef = useRef<QuickSearchHandle>(null);

  const handleOpenQuickSearch = useCallback(() => {
    quickSearchRef.current?.open();
  }, []);

  // Bootstrap query to get all accessible schools and roles
  const { data: bootstrapData, loading: bootstrapLoading, error: bootstrapError } = useBootstrapQuery();

  const appPortal = bootstrapData?.currentUser?.appPortal;
  const currentUserId = bootstrapData?.currentUser?.id;
  const { selectedSchoolId, selectedRole, selectedSchoolYearId, setSelection } = useRoleSelection(appPortal, schoolSlug as string | null, yearSlug as string | null);
  const roleContext = useRoleContext(appPortal, selectedSchoolId, selectedRole, selectedSchoolYearId);
  const roleGroup = roleGroupFor(roleContext.selectedRole);
  const sidebar = useSidebarLayout(currentUserId, roleGroup, roleGroup !== 'teacher');
  const savedViews = useMemo(
    () => allViews(sidebar.layout).map((view) => ({ id: view.id, group: view.group, name: view.name, url: view.url })),
    [sidebar.layout]
  );
  const isAdmin = roleGroup === 'admin';
  // A teacher's sidebar is the work in front of them: the class or the teaching
  // group they are inside, which the app decides, not a list they arrange. With
  // nothing left for them to rearrange, the customizer only offered rows that
  // did nothing, so teachers do not get it at all.
  const canCustomizeSidebar = roleGroup !== 'teacher';

  // A parent navigates by child, so the sidebar needs to know who they are.
  // Only families pay for this query.
  const isFamily = roleGroup === 'family';
  const { data: familyNavData } = useMyFamilyQuery(
    isFamily ? selectedSchoolId ?? undefined : undefined,
    isFamily ? selectedSchoolYearId ?? undefined : undefined
  );
  const studentLinks = useMemo(
    () => (familyNavData?.myFamily?.children ?? []).map((child) => ({
      personId: child.person.id,
      name: child.person.fullName,
      path: `/app/${schoolSlug}/students/${child.person.id}`,
    })),
    [familyNavData, schoolSlug]
  );

  const openCustomizer = useCallback(() => setCustomizerOpen(true), []);

  // Navigate to selected school if URL slug doesn't match and we have a valid school
  React.useEffect(() => {
    if (!bootstrapLoading && roleContext.selectedSchool && schoolSlug) {
      if (roleContext.selectedSchool.slug !== schoolSlug) {
        navigate(`/app/${roleContext.selectedSchool.slug}`, { replace: true });
      }
    }
  }, [roleContext.selectedSchool, schoolSlug, navigate, bootstrapLoading]);

  // Load school data for the selected school
  const { data: schoolData, loading: schoolLoading, error: schoolError, refetch: refetchSchool } = useAppQuery(selectedSchoolId, bootstrapLoading);

  // Unmask the SidebarFragment so child components get typed fields (id, name, slug, schoolYears)
  const school = useMemo(
    () => schoolData?.school ? readFragment(SidebarFragment, schoolData.school) : null,
    [schoolData?.school]
  );

  // The year the app is working in, which class links are filed under and which
  // answers whether this person teaches at all.
  const selectedYear = useMemo(
    () => (school?.schoolYears ?? []).find((year) => year.id === selectedSchoolYearId) ?? null,
    [school?.schoolYears, selectedSchoolYearId]
  );

  // The years the family is enrolling in, for the way on from the closed
  // portal: each one whose checklist needs the family, and the selected year
  // while it's enrolling, whatever is left to do on it. An enrolled year counts
  // while a step is still waiting on the family (a sibling added since, with
  // an updated agreement to sign), as the server decides for the office's
  // reminder.
  const enrollingYears = useMemo(() => {
    const family = familyNavData?.myFamily;
    const stillEnrolling = (year: { access: string; checklist?: { waitingOnFamily: boolean } | null } | null | undefined) =>
      year?.access === 'ENROLLING' || (year?.access === 'ENROLLED' && !!year.checklist?.waitingOnFamily);
    const years = (family?.yearsNeedingYou ?? [])
      .filter(stillEnrolling)
      .map(({ schoolYear }) => ({ id: schoolYear.id, name: schoolYear.name, slug: schoolYear.slug }));
    if (stillEnrolling(family?.familyEnrollmentForYear) && selectedYear && !years.some((year) => year.id === selectedYear.id)) {
      years.push({ id: selectedYear.id, name: selectedYear.name, slug: selectedYear.slug });
    }
    return years;
  }, [familyNavData, selectedYear]);

  // An administrator who teaches nothing this year has no Teaching link. The
  // answer rides along with the years the sidebar already loads, so switching
  // years settles it without another round trip.
  const teachesAClass = !!selectedYear?.viewerTeaches;

  // Anyone who teaches navigates by class, so the sidebar needs to know which
  // ones — the way a parent's sidebar needs to know their children. Only one
  // term's classes are listed: the term containing today, else the next term to
  // start (defaultTermId).
  const { data: teachingNavData } = useSidebarClassesQuery(
    selectedSchoolYearId ?? undefined,
    !(roleGroup === 'teacher' || teachesAClass)
  );
  const classLinks = useMemo(() => {
    const offerings = teachingNavData?.schoolYear?.myClassOfferings ?? [];
    const yearPathSlug = selectedYear?.slug;
    if (offerings.length === 0 || !yearPathSlug) return [];
    const termId = defaultTermId(termsFromClasses(offerings));
    const inTerm = termId ? classesInTerm(offerings, termId) : offerings;
    return inTerm.flatMap((offering) => {
      const courseSlug = offering.course?.slug;
      if (!courseSlug) return [];
      return [{
        id: offering.id,
        name: offering.displayName,
        path: `/app/${schoolSlug}/years/${yearPathSlug}/courses/${courseSlug}/classes/${offering.id}`,
      }];
    });
  }, [teachingNavData, schoolSlug, selectedYear?.slug]);

  // Close sidebar when navigating (for mobile)
  const handleSidebarNavigation = () => {
    setSidebarOpen(false);
  };

  if (bootstrapLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (bootstrapError) {
    if (bootstrapError.message?.includes('authenticate') || (bootstrapError.networkError as { statusCode?: number } | null)?.statusCode === 401) {
      window.location.href = '/users/sign_in';
      return null;
    }
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-destructive">Error: {bootstrapError.message}</div>
      </div>
    );
  }

  if (!bootstrapData?.currentUser) {
    window.location.href = '/users/sign_in';
    return null;
  }

  if (!appPortal || !roleContext.selectedSchool) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="w-full max-w-sm space-y-4 rounded-lg border bg-card p-6 text-center shadow-sm">
          <div className="text-muted-foreground">No accessible schools found</div>
          <SignOutButton />
        </div>
      </div>
    );
  }

  if (schoolLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-muted-foreground">Loading school data...</div>
      </div>
    );
  }

  if (schoolError) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-destructive">Error loading school: {schoolError.message}</div>
      </div>
    );
  }

  if (!school) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-muted-foreground">School not found</div>
      </div>
    );
  }

  const outletContext = {
    school,
    currentUser: bootstrapData.currentUser,
    roleContext,
    setSelection,
    refetchSchool,
    isAdmin,
    canCustomizeSidebar,
    openSidebarCustomizer: openCustomizer,
  };

  // The family experience is something a school turns on. Until it does, a
  // parent who signs in is told so plainly rather than being dropped into a
  // dashboard the school has not decided to run yet. Enrolling is the
  // exception, as the setting promises: the enroll pages open on their own,
  // without the portal around them, and the closed screen points the way to
  // any year the family is enrolling in.
  if (roleGroup === 'family' && !school.parentAlertsSetting?.enabled) {
    if (onEnrollPage) {
      return (
        <div className="min-h-screen bg-background">
          <header className="flex items-center justify-between gap-3 border-b border-border bg-muted/50 px-4 py-2">
            <span className="min-w-0 truncate font-medium">{school.name}</span>
            <SignOutButton className="shrink-0" />
          </header>
          <main>
            <Outlet context={outletContext} />
          </main>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-6 text-center shadow-sm">
          <h1 className="text-lg font-medium">{school.name}</h1>
          <p className="text-sm text-muted-foreground">
            {school.name} has not opened the family portal yet. Enrollment and application
            links the school emails you still work; anything else, the school office can help
            with.
          </p>
          {enrollingYears.map((year) => (
            <Button key={year.id} asChild className="w-full">
              <Link to={`/app/${school.slug}/years/${year.slug}/enroll`}>Continue enrolling for {year.name}</Link>
            </Button>
          ))}
          <SignOutButton />
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout bg-background">
      <header className="app-header bg-muted/50 border-b border-border">
        <Topbar
          school={school}
          roleContext={roleContext}
          setSelection={setSelection}
          onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
          sidebarOpen={sidebarOpen}
          onSearchClick={handleOpenQuickSearch}
        />
      </header>
      <div className="app-body">
        {sidebarOpen && (
          <div
            className="sidebar-overlay md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <aside
          className={`app-sidebar border-r border-border bg-background md:bg-muted/30 ${sidebarOpen ? 'open' : ''}`}
        >
          <Sidebar
            school={school}
            studentLinks={studentLinks}
            classLinks={classLinks}
            teachesAClass={teachesAClass}
            roleContext={roleContext}
            onNavigate={handleSidebarNavigation}
            layout={sidebar.layout}
            onOpenSettings={() => navigate(`/app/${school.slug}/settings`)}
          />
        </aside>
        <main className="app-main bg-background">
          <Outlet context={outletContext} />
        </main>
      </div>
      <QuickSearch
        ref={quickSearchRef}
        schoolYearId={roleContext?.selectedSchoolYear?.id}
        schoolSlug={school?.slug}
        roleContext={roleContext}
        customNavItems={savedViews}
        showTrigger={false}
      />
      {canCustomizeSidebar && <SidebarCustomizer
        open={customizerOpen}
        onOpenChange={setCustomizerOpen}
        layout={sidebar.layout}
        roleGroup={roleGroup}
        onSave={sidebar.setLayout}
        defaultFor={() => defaultLayout(roleGroup)}
      />}
    </div>
  );
}

/**
 * The sidebar and the routed page are siblings, so a page that wants to put
 * something in the sidebar — a class showing its sections — needs a holder
 * above them both. That is all this wrapper is.
 */
function AppLayout() {
  return (
    <ContextNavProvider>
      <AppLayoutInner />
    </ContextNavProvider>
  );
}

export default AppLayout;
